/*
"Partagés avec moi" — list of cards another user has shared with the
authenticated caller via POST /api/contents/:id/share. Rows where
viewedAt is null are highlighted; opening the card via the link
clears the highlight on the next refresh (the server stamps viewedAt
on the first read through canReadCard).
*/

import { useCallback, useEffect, useState, type JSX } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../lib/api";
import { ErrorState } from "../components/ErrorState";
import { Skeleton, SkeletonRows } from "../components/Skeleton";

const kPageSize	= 50;	// Rows per page on this view

interface SharedRow
{
	sharingId:		string;	// Sharing row id (stable across page refreshes)
	sharedAt:		number;	// Sharing.createdAt (ms since epoch)
	sharedByEmail:	string;	// Email of the user who shared with me
	message:		string | null;	// Optional note attached at share time
	viewedAt:		number | null;	// First-view timestamp; null = unread
	id:				string;	// CardContent id
	serialNumber:	string;
	cardType:		string;
	dataModel:		string;
	readDate:		number;
	folderPath:		string;
	createdAt:		number;
	updatedAt:		number;
}

interface SharedPageResponse
{
	items:		SharedRow[];
	total:		number;
	page:		number;
	pageSize:	number;
}

export function SharedPage(): JSX.Element
{
	const { t }		= useTranslation("shared");
	const { t: tc }	= useTranslation("common");

	const [fRows,  fSetRows]	= useState<SharedRow[] | null>(null);
	const [fTotal, fSetTotal]	= useState<number>(0);
	const [fPage,  fSetPage]	= useState<number>(1);
	const [fError, fSetError]	= useState<string | null>(null);

	const fLoad = useCallback((inPage: number) =>
	{
		fSetError(null);
		api<SharedPageResponse>("GET", `/contents/shared?page=${inPage}&pageSize=${kPageSize}`)
			.then((vResp) =>
			{
				fSetRows(vResp.items);
				fSetTotal(vResp.total);
				fSetPage(vResp.page);
			})
			.catch((vErr: unknown) =>
			{
				fSetError(vErr instanceof ApiError ? vErr.message : tc("error_network"));
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => { fLoad(1); }, [fLoad]);

	if (fError !== null)
	{
		return <ErrorState message={fError} onRetry={() => fLoad(fPage)} />;
	}

	if (fRows === null)
	{
		return (
			<div className="card">
				<Skeleton variant="title" widthPercent={35} />
				<SkeletonRows count={5} />
			</div>
		);
	}

	if (fRows.length === 0)
	{
		return (
			<div className="card">
				<h2>{t("title_count", { total: 0 })}</h2>
				<p className="muted">{t("no_shares")}</p>
			</div>
		);
	}

	const vTotalPages = Math.max(1, Math.ceil(fTotal / kPageSize));

	return (
		<div className="card">
			<h2>{t("title_count", { total: fTotal })}</h2>
			<table>
				<thead>
					<tr>
						<th>{t("col_serial")}</th>
						<th>{t("col_shared_by")}</th>
						<th>{t("col_received_on")}</th>
						<th>{t("col_type")}</th>
						<th>{t("col_model")}</th>
						<th>{t("col_message")}</th>
					</tr>
				</thead>
				<tbody>
					{fRows.map((r) => (
						<tr key={r.sharingId} className={r.viewedAt === null ? "row--unread" : undefined}>
							<td>
								<Link to={`/cards/${r.id}`} className="cards-row__serial">
									<code>{r.serialNumber}</code>
									{r.viewedAt === null
										? <span className="badge badge--unread" title={t("new_badge")}>●</span>
										: null}
								</Link>
							</td>
							<td>{r.sharedByEmail}</td>
							<td>{formatDateTime(r.sharedAt)}</td>
							<td>{r.cardType}</td>
							<td>{r.dataModel}</td>
							<td className="muted">{r.message ?? <span className="muted">{tc("dash")}</span>}</td>
						</tr>
					))}
				</tbody>
			</table>

			<div className="pager">
				<button
					type="button"
					className="button button--ghost"
					disabled={fPage <= 1}
					onClick={() => fLoad(fPage - 1)}
				>
					{tc("prev")}
				</button>
				<span className="muted">{tc("page_of", { page: fPage, total: vTotalPages })}</span>
				<button
					type="button"
					className="button button--ghost"
					disabled={fPage >= vTotalPages}
					onClick={() => fLoad(fPage + 1)}
				>
					{tc("next")}
				</button>
			</div>
		</div>
	);
}

/*
Formats a ms-since-epoch timestamp to "YYYY-MM-DD HH:MM" in the local TZ.
*/
function formatDateTime(
	inMs:	number	// Timestamp in milliseconds
): string
{
	const vDate = new Date(inMs);	// Local Date
	const pad = (n: number) => String(n).padStart(2, "0");

	return `${vDate.getFullYear()}-${pad(vDate.getMonth() + 1)}-${pad(vDate.getDate())} ${pad(vDate.getHours())}:${pad(vDate.getMinutes())}`;
}
